import { Dictionary, DistributiveOmit, Optional, objectMap } from '@direct-frontend/stdlib';

import { getFormStoreSingleton } from '../store/store';
import { BaseValidationError, ValidationErrorSource, ValidationState, ValidatorType } from '../types';
import { ValueContainer, createValueContainer } from '../utils/createValueContainer';

import { ArrayField } from './createArrayField';
import { ObjectField } from './createObjectField';
import { PrimitiveField } from './createPrimitiveField';
import { BaseUnionConstituent, InferKeyValue, UnionField } from './createUnionField';
import { ArrayItemNotInitializedError } from './getFieldInstance';
import { BaseField, BaseFieldSelectorsMap, BaseFields, BoundFieldSelectors } from './types';
import { stringifyFieldIdPath } from './utils/stringifyFieldIdPath';
import { ValidationOptions } from './validation';
import { bindFieldSelectors } from './bindFieldSelectors';
import { getDefaultFieldState } from '../store/utils/getDefaultFieldState';
import { getActualUnionFieldConstituent } from './utils/getActualUnionFieldConstituent';
import { assertFormInitialized } from '../form/utils/assertFormInitialized';
import { FormActionsMap, $internals as $formInternals } from '../form/types';
import { FieldValidationMeta } from '../store/types';

/** Ключ, за которым скрыты внутренние методы `fieldApi` */
export const $internals = Symbol('$internals');

/** Методы для работы с любым полем в валидаторах */
export interface BaseFieldApi<FieldValidationError extends BaseValidationError, FieldContext, FieldSelectors = never> {
    /** Параметры формы, в которой используется текущее поле */
    form: {
        /** Идентификатор формы */
        id: string;
    };
    /** Добавляет ошибку в состояние поля */
    addValidationError: (error: DistributiveOmit<FieldValidationError, 'meta'>) => void;
    /** Добавляет ошибки в состояние поля */
    addValidationErrors: (errors: DistributiveOmit<FieldValidationError, 'meta'>[]) => void;
    /** Возвращает текущее значение контекста поля */
    getContext: () => FieldContext;
    /** Селекторы поля */
    selectors: BoundFieldSelectors<FieldSelectors>;
    /** Внутренние методы, которые не доступны в публичном API */
    [$internals]: {
        getOptions: FieldApiOptionsContainer['get'];
        updateOptions: FieldApiOptionsContainer['set'];
    };
}

/** Методы для работы с "объектным" полем в валидаторах */
export interface ObjectFieldApi<
    InnerFields extends BaseFields,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FieldSelectors extends BaseFieldSelectorsMap,
> extends BaseFieldApi<FieldValidationError, FieldContext, FieldSelectors> {
    /** Вложенные поля объекта */
    fields: {
        [FieldName in keyof InnerFields & string]: FieldApi<InnerFields[FieldName]> & {
            /** Проверяет есть ли в состоянии поля хотя бы одна ошибка */
            hasValidationErrors: () => Promise<boolean>;
        };
    };
}

/** Методы для работы с полем-массивом в валидаторах */
export interface ArrayFieldApi<
    InnerField extends BaseField,
    FieldValidationError extends BaseValidationError,
    FieldContext,
    FieldSelectors extends BaseFieldSelectorsMap,
> extends BaseFieldApi<FieldValidationError, FieldContext, FieldSelectors> {
    /** Получает объект `fieldApi` для элемента массива по заданному индексу */
    fieldAt: (index: number) => FieldApi<InnerField> & {
        /** Проверяет есть ли в состоянии поля хотя бы одна ошибка */
        hasValidationErrors: () => Promise<boolean>;
    };
}

/** Методы для работы с полем-массивом в валидаторах */
export type UnionFieldApi<Key extends string, UnionConstituent extends BaseUnionConstituent<Key>> =
    UnionConstituent extends ObjectField<
        infer InnerFields,
        infer FieldValidationError,
        infer _,
        infer FieldContext,
        infer __,
        infer FieldSelectors,
        infer ___
    >
        ? ObjectFieldApi<InnerFields, FieldValidationError, FieldContext, FieldSelectors> & {
              /** Текущее значения поля-ключа, которое может быть использовано для сужения типа инстанса поля */
              key: InferKeyValue<Key, InnerFields>;
          }
        : never;

// Возвращает тип `fieldApi` для поля заданного типа
export type FieldApi<Field extends BaseField> =
    Field extends PrimitiveField<
        infer _,
        infer FieldValidationError,
        infer __,
        infer FieldContext,
        infer ___,
        infer FieldSelectors,
        infer ____
    >
        ? BaseFieldApi<FieldValidationError, FieldContext, FieldSelectors>
        : Field extends ObjectField<
                infer InnerFields,
                infer FieldValidationError,
                infer _,
                infer FieldContext,
                infer ___,
                infer FieldSelectors,
                infer ____
            >
          ? ObjectFieldApi<InnerFields, FieldValidationError, FieldContext, FieldSelectors>
          : Field extends ArrayField<
                  infer InnerField,
                  infer FieldValidationError,
                  infer _,
                  infer FieldContext,
                  infer ___,
                  infer FieldSelectors,
                  infer ____
              >
            ? ArrayFieldApi<InnerField, FieldValidationError, FieldContext, FieldSelectors>
            : Field extends UnionField<infer Key, infer UnionConstituents, infer __>
              ? UnionFieldApi<Key, UnionConstituents[number]>
              : never;

/** Параметры работы объекта `fieldApi` */
type FieldApiOptions = {
    /**
     * `fieldApi` имеет два типа хранилища с которыми он умеет работает:
     * 1. in-memory – все ошибки сохраняются в объект в памяти и читаются из него же
     *    Используется при выполнении синхронных валидаторов, чтобы оптимизировать
     *    сохранение ошибок в слайс и добавить их все за один экшен
     * 2. slice – все ошибки сохраняются сразу же в слайс и читаются из него же
     *    Используется при выполнение асинхронных валидаторов, чтобы каждый валидатор
     *    получал доступ к актуальному состоянию ошибок, иначе работа с временным объектом и момент
     *    записи в слайс будет неконтроллируемым
     */
    storageType: 'in-memory' | 'slice';
    /**
     * Источник создания `fieldApi`
     * Объект может быть создан как для текущего поля, так и для дочернего из родительских валидаторов
     */
    source: ValidationErrorSource;
    /**
     * Метаданные валидации - текущий AbortController и промисы запущенных валидаторов
     * Являются более приоритетными, чем метаданные из `validationState`
     * Используются, если путь до поля изменился уже в процессе работы асинхронного валидатора
     */
    fieldsValidationMeta?: Dictionary<Optional<FieldValidationMeta>>;
};

/**
 * Параметры работы `fieldApi` хранятся в контейнере по двум причинам:
 * 1. Определить тип валидатора (синхронный или асинхронный) можно только после его вызова
 * 2. Спустя время после создания `fieldApi` путь поля может быть изменен (например, при сдвигах элементов массива)
 * Хранение в контейнере позволяет динамически изменять параметры уже созданных `fieldApi`
 */
export type FieldApiOptionsContainer = ValueContainer<FieldApiOptions>;

// Возвращает методы, которые есть в любом `fieldApi`
function createBaseFieldApi<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validatorType: ValidatorType,
    validationOptions: ValidationOptions<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    validationState: ValidationState<FormValidationError>,
    fieldApiOptionsContainer: FieldApiOptionsContainer
) {
    const { addFieldValidationErrors, getFieldContext, getFieldState } = getFormStoreSingleton(
        validationOptions.form.name
    );

    const inMemoryFieldKey = validationOptions.fieldId;
    const inMemoryValidationErrorsMap =
        validationState.fieldsValidation[inMemoryFieldKey]?.errorsMap ??
        (validationState.fieldsValidation[inMemoryFieldKey] = {
            errorsMap: getDefaultFieldState().errorsMap,
            meta: getDefaultFieldState().validationMeta,
        }).errorsMap;

    function getAbortController() {
        const { source, fieldsValidationMeta } = fieldApiOptionsContainer.get();

        // Если источник запуска валидации – форма, то проверяем была ли отменена валидация всей формы
        if (source.type === 'form') {
            return validationState.formValidationMeta?.[validatorType]?.controller;
        }

        // Иначе источником является какое-то поле, поэтому проверяем была ли отменена валидация этого поля
        // Валидация поля, в которое добавляется ошибка при этом может быть не отменена,
        // так как оно может находиться в другой ветке дерева полей формы
        return (
            fieldsValidationMeta?.[source.fieldKey]?.[validatorType].controller ??
            validationState.fieldsValidation?.[source.fieldKey]?.meta?.[validatorType]?.controller
        );
    }

    return {
        form: {
            id: validationOptions.form.id,
        },

        getContext: () => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });

            return getFieldContext({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                fieldId: validationOptions.fieldId,
            });
        },

        addValidationError: (error: DistributiveOmit<FormValidationError, 'meta'>) => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });

            const { source } = fieldApiOptionsContainer.get();
            if (fieldApiOptionsContainer.get().storageType === 'in-memory') {
                inMemoryValidationErrorsMap[validatorType].push({ ...error, meta: { source } });
                return;
            }

            // Если мы находимся в асинхронном валидаторе, то перед сохранением ошибки
            // нужно проверить не была ли валидация отменена
            if (getAbortController()?.signal.aborted) {
                return;
            }

            addFieldValidationErrors({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                fieldId: validationOptions.fieldId,
                validatorType,
                validationErrors: [{ ...error, meta: { source } }],
            });
        },

        addValidationErrors: (errors: DistributiveOmit<FormValidationError, 'meta'>[]) => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });

            const { source } = fieldApiOptionsContainer.get();

            // TypeScript считает, что тип может быть более узким и не содержать `form`, но на деле это не так
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            const errorsWithMeta = errors.map(error => ({ ...error, meta: { source } })) as FormValidationError[];

            if (fieldApiOptionsContainer.get().storageType === 'in-memory') {
                inMemoryValidationErrorsMap[validatorType].push(...errorsWithMeta);
                return;
            }

            // Если мы находимся в асинхронном валидаторе, то перед сохранением ошибки
            // нужно проверить не была ли валидация отменена
            if (getAbortController()?.signal.aborted) {
                return;
            }

            addFieldValidationErrors({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                fieldId: validationOptions.fieldId,
                validatorType,
                validationErrors: errorsWithMeta,
            });
        },

        hasValidationErrors: async () => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });

            const fieldId = validationOptions.fieldId;

            // Так как метод асинхронный, то в нем не имеет смысла работать в in-memory режиме
            let fieldState = getFieldState({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                fieldId,
            });
            if (!fieldState) {
                return false;
            }

            // Дожидаемся завершения выполнения всех валидаторов текущего типа
            await Promise.allSettled(fieldState.validationMeta[validatorType].promises ?? []);

            // Если за это время валидатор был отменен, то сразу выходим
            if (getAbortController()?.signal.aborted) {
                return false;
            }

            // После завершения всех валидаторов в стейте актуальный список ошибок
            fieldState = getFieldState({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                fieldId,
            });

            if (!fieldState?.errorsMap) {
                return false;
            }

            return (
                fieldState.errorsMap.onChange.length > 0 ||
                fieldState.errorsMap.onBlur.length > 0 ||
                fieldState.errorsMap.onSubmit.length > 0
            );
        },

        selectors: bindFieldSelectors(validationOptions.form, validationOptions.field, validationOptions.fieldId),

        [$internals]: {
            getOptions: fieldApiOptionsContainer.get,
            updateOptions: (value: Partial<FieldApiOptions>) => {
                fieldApiOptionsContainer.set(({ source, ...currentValue }) => {
                    // Если источником `fieldApi` является форма, то при обновлении параметров ничего не делаем
                    if (source.type === 'form') {
                        return { ...currentValue, ...value, source };
                    }

                    return {
                        ...currentValue,
                        ...value,
                        source: {
                            type: 'field',
                            fieldKey: source.fieldKey,
                        },
                    };
                });
            },
        },
    };
}

// Возвращает `fieldApi` для произвольного поля
export function createFieldApi<
    Field extends BaseField,
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validatorType: ValidatorType,
    validationOptions: ValidationOptions<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue
    >,
    validationState: ValidationState<FormValidationError>,
    fieldApiOptionsContainer: FieldApiOptionsContainer = createValueContainer({
        storageType: 'in-memory',
        source: {
            type: 'field',
            fieldKey: validationOptions.fieldId,
        },
    })
): FieldApi<Field> {
    if (validationOptions.field.type === 'object') {
        // Так как FieldApi – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        return {
            ...createBaseFieldApi(validatorType, validationOptions, validationState, fieldApiOptionsContainer),
            fields: objectMap(validationOptions.field.fields, (innerField, innerFieldName) =>
                createFieldApi(
                    validatorType,
                    {
                        ...validationOptions,
                        field: innerField,
                        fieldId: stringifyFieldIdPath([validationOptions.fieldId, innerFieldName]),
                    },
                    validationState,
                    fieldApiOptionsContainer
                )
            ),
        } as unknown as FieldApi<Field>;
    }

    if (validationOptions.field.type === 'union') {
        const { getFieldValue } = getFormStoreSingleton(validationOptions.form.name);

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Значение юнион-поля всегда объект
        const value = getFieldValue({
            formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            fieldId: validationOptions.fieldId,
        }) as Dictionary<unknown>;

        const objectField = getActualUnionFieldConstituent(validationOptions.field, value);

        return {
            ...createFieldApi(
                validatorType,
                { ...validationOptions, field: objectField },
                validationState,
                fieldApiOptionsContainer
            ),
            key: value[validationOptions.field.key],
        };
    }

    if (validationOptions.field.type === 'array') {
        const innerField = validationOptions.field.field;
        const { getArrayItemIdByIndex } = getFormStoreSingleton(validationOptions.form.name);

        // Так как FieldApi – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        return {
            ...createBaseFieldApi(validatorType, validationOptions, validationState, fieldApiOptionsContainer),
            fieldAt: (index: number) => {
                assertFormInitialized({
                    formName: validationOptions.form.name,
                    formId: validationOptions.form.id,
                    formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                });

                const arrayItemIdPath = getArrayItemIdByIndex({
                    formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                    arrayFieldId: validationOptions.fieldId,
                    index,
                });

                // Проверяем есть ли в массиве элемент по заданному индексу
                if (arrayItemIdPath) {
                    return createFieldApi(
                        validatorType,
                        {
                            ...validationOptions,
                            field: innerField,
                            fieldId: arrayItemIdPath,
                        },
                        validationState,
                        fieldApiOptionsContainer
                    );
                }

                // Если такого элемента нет, то выбрасываем исключение
                throw new ArrayItemNotInitializedError('Array item not initialized', {
                    cause: null,
                    additional: {
                        form: {
                            name: validationOptions.form.name,
                            id: validationOptions.form.id,
                        },
                        field: {
                            path: validationOptions.fieldId,
                            index,
                        },
                    },
                });
            },
        } as unknown as FieldApi<Field>;
    }

    // Так как FieldApi – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return createBaseFieldApi(
        validatorType,
        validationOptions,
        validationState,
        fieldApiOptionsContainer
    ) as unknown as FieldApi<Field>;
}
